import { useEffect, useState } from 'react';
import Modal from '../ui/Modal';
import DocumentPreviewModal from '../ui/DocumentPreviewModal';
import PdfDocumentPreview from '../ui/PdfDocumentPreview';
import '../../pages/css/StaffDocumentsReviewPage.css';
import CountrySelect from '../ui/CountrySelect';
import { intakeLabels, categoryLabels, intakeReviewValueError } from '../../utils/clientDocumentIntake';
const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
const labelFor = field => (intakeLabels[field] || field).replace(' (KYC)', '');
function display(field, value) {
  if (!value) return 'Leave blank';
  if (['country', 'nationality'].includes(field) && /^[A-Z]{2}$/.test(value)) return countryNames.of(value);
  if (field === 'clientType') return value === 'individual' ? 'Individual' : 'Entity';
  if (['dateOfBirth', 'incorporationDate'].includes(field) && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return value;
}
const editable = row => !row.existingValueDiffers && (row.uncertain || row.issues.length > 0) && row.options.length <= 1 && row.field !== 'clientType';

export default function ClientDocumentIntakeReview({ review, open = true, onClose, onResolve, documentsOnly = false }) {
  const included = review.documents.map(d => d.id);
  const [choices, setChoices] = useState({});
  const [edited, setEdited] = useState({});
  const [preview, setPreview] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState(false);
  const documentTypes = Object.fromEntries(review.documents.map(d => [d.id, categoryLabels[d.category] || 'Document']));
  const rows = review.review.filter(row => row.options.some(o => included.includes(o.documentId)) || row.issues.some(i => included.includes(i.documentId)));
  const sourceId = row => row.options.find(o => included.includes(o.documentId))?.documentId || row.issues.find(i => included.includes(i.documentId))?.documentId;
  const initialValue = row => edited[row.field] ?? (row.currentValue || row.options.find(o => included.includes(o.documentId))?.value || '');
  const selectedKey = row => row.options.some(o => included.includes(o.documentId) && `${o.documentId}:${o.value}` === choices[row.field]) ? choices[row.field] : '';
  const errors = Object.fromEntries(rows.filter(editable).map(row => [row.field, intakeReviewValueError(row.field, initialValue(row))]));

  useEffect(() => {
    if (!open || !preview?.file) { setPreviewUrl(''); return; }
    let url;
    setPreviewError(false);
    try {
      const bytes = Uint8Array.from(atob(preview.file.dataBase64), c => c.charCodeAt(0));
      url = URL.createObjectURL(new Blob([bytes], { type: preview.file.contentType }));
      setPreviewUrl(url);
    } catch { setPreviewUrl(''); setPreviewError(true); }
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [preview, open]);

  useEffect(() => {
    if (!open || !preview) return;
    const onKeyDown = event => { if (event.key === 'Escape') setPreview(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, preview]);

  const needsChoice = documentsOnly && rows.some(row => !selectedKey(row));

  function finish() {
    if (needsChoice || Object.values(errors).some(Boolean)) return;
    const resolved = { ...choices };
    rows.filter(editable).forEach(row => {
      // Unchanged existing details need no replacement/provenance change.
      if (edited[row.field] === undefined && row.currentValue) return;
      resolved[row.field] = { documentId: sourceId(row), value: initialValue(row).trim() };
    });
    onResolve(included, resolved);
  }
  if (preview) return <DocumentPreviewModal
    open={open} title={documentTypes[preview.id]}
    file={{ contentType: preview.file.contentType, originalName: documentTypes[preview.id], downloadURL: previewUrl }}
    loading={!previewUrl && !previewError} loadingText="Loading document..."
    pdfRenderer={PdfDocumentPreview}
    showOpenInNewTab={false} shellHeight="min(76vh, 720px)"
    style={{ '--doc-preview-stack-height': 'min(76vh, 720px)' }}
    onClose={() => setPreview(null)} />;

  return <Modal open={open} title="Choose the correct details"
    className="clientIntakeReview" onClose={onClose}
    footer={<button type="button" className="btn btn-sm btn-modal-save" disabled={needsChoice || Object.values(errors).some(Boolean)}
      onClick={finish}>Done</button>}>
      <p className="intakeReviewIntro">{documentsOnly ? 'Your documents contain different details. Choose the correct information below.' : 'Some details differ or need checking. Choose what to keep.'}</p>
      {rows.map(row => <section className="intakeReviewField" key={row.field} aria-labelledby={`intake-label-${row.field}`}>
        <div className="intakeReviewFieldHeading">
          <h3 id={`intake-label-${row.field}`}>{labelFor(row.field)}</h3>
          {review.documents.filter(doc => doc.file && [...row.options, ...row.issues].some(option => option.documentId === doc.id)).map(doc =>
            <button key={doc.id} type="button" className="intakeReviewTextButton" aria-label={`View ${documentTypes[doc.id]}`} title={`View ${documentTypes[doc.id]}`} onClick={() => setPreview(doc)}><i className="bi bi-eye" aria-hidden="true" /></button>)}
        </div>
        {editable(row) ? <div className="intakeReviewEdit">
          <label htmlFor={`intake-${row.field}`}>Check this detail</label>
          {['country', 'nationality'].includes(row.field)
            ? <CountrySelect inputId={`intake-${row.field}`} value={initialValue(row)} onChange={value => setEdited(prev => ({ ...prev, [row.field]: value || '' }))} />
            : <input id={`intake-${row.field}`} className="form-control" aria-label={labelFor(row.field)}
                type={['dateOfBirth', 'incorporationDate'].includes(row.field) ? 'date' : row.field === 'contactEmail' ? 'email' : 'text'}
                value={initialValue(row)} maxLength={2000} aria-invalid={!!errors[row.field]}
                onChange={event => setEdited(prev => ({ ...prev, [row.field]: event.target.value }))} />}
          {errors[row.field] && <small role="alert">{errors[row.field]}</small>}
        </div> : <div className="intakeReviewOptions" role="group" aria-label={labelFor(row.field)}>
          {[...(documentsOnly ? [] : [{ key: '', value: row.currentValue, source: 'Current details' }]), ...row.options.filter(o => included.includes(o.documentId)).map(o => ({ key: `${o.documentId}:${o.value}`, value: o.value, source: `From ${documentTypes[o.documentId]}` }))].map(option => <button
            type="button" className="intakeReviewOption" key={option.key} aria-pressed={selectedKey(row) === option.key}
            onClick={() => setChoices(prev => ({ ...prev, [row.field]: option.key }))}>
            <span className="intakeReviewOptionSource">{option.source}</span>
            <span className="intakeReviewOptionValue"><i className={selectedKey(row) === option.key ? 'bi bi-check-circle-fill' : 'bi bi-circle'} aria-hidden="true" />{display(row.field, option.value)}</span>
          </button>)}
        </div>}
      </section>)}
      {!rows.length && <p className="intakeReviewIntro">No details need checking. Click Done to continue.</p>}
  </Modal>;
}
